import {
  Directive,
  HostListener,
  ElementRef,
  OnInit,
  NgZone,
  forwardRef,
  Input,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appPriceFormat]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PriceFormatDirective),
      multi: true
    }
  ]
})
export class PriceFormatDirective implements ControlValueAccessor, OnInit, OnChanges {
  private onChange: any;
  _precision = 8;
  _max: undefined | number = undefined;

  @Input() rawValue: string | null = null;
  @Input() set precision(value: number | undefined) {
    if (value !== undefined && value !== null) {
      this._precision = value;
    }
  }

  @Input() set max(value: number | undefined | null) {
    this._max = value ?? undefined;
  }

  constructor(
    private el: ElementRef,
    private zone: NgZone
  ) {}

  ngOnInit() {
    this.el.nativeElement.value = this.format(this.el.nativeElement.value);
  }

  @HostListener('input', ['$event.target.value'])
  onInput(value: string) {
    this.onChange(this.unformat(value));
    this.el.nativeElement.value = this.format(value);
  }

  writeValue(value: any): void {
    this.zone.run(() => {
      this.el.nativeElement.value = this.format(value);
    });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  scientificToDecimal(sciNumber: string): string {
    if (!sciNumber.includes('e')) {
      return sciNumber;
    }
    const sign = sciNumber[0] === '-' ? '-' : '';
    const regex = /(\d+(\.\d*)?)(e([+-]?\d+))?/i;
    const match = regex.exec(sciNumber);

    if (!match) {
      return sciNumber;
    }

    const fraction = match[1] || '';
    const exponent = parseInt(match[4] || '0', 10);

    // Decimal point index
    const decimalIndex = fraction.indexOf('.');
    let fractionWithoutDot = fraction.replace('.', '');
    let padding = '0';

    // If there is a decimal point, adjust for it
    if (decimalIndex !== -1) {
      padding = padding.repeat(fraction.length - decimalIndex - 1);
      fractionWithoutDot = fraction.replace('.', '');
    }

    // Add zeros based on the exponent
    if (exponent > 0) {
      return sign + fractionWithoutDot + padding + '0'.repeat(exponent - padding.length);
    } else if (exponent < 0) {
      const pad = '0'.repeat(-exponent - 1);
      return sign + '0.' + pad + fractionWithoutDot;
    }

    return sign + fractionWithoutDot;
  }

  private convertFaDigits(input: string): string {
    const finglishToEnglish = new Map<string, string>(
      Object.entries({
        '۰': '0',
        '۹': '9',
        '۸': '8',
        '۷': '7',
        '۶': '6',
        '۵': '5',
        '۴': '4',
        '۳': '3',
        '۲': '2',
        '۱': '1'
      })
    );

    return input.replace(/[۰۹۸۷۶۵۴۳۲۱]/g, (c) => finglishToEnglish.get(c)!);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['precision'] && !changes['precision'].isFirstChange()) {
      this.el.nativeElement.value = this.format(this.rawValue?.toString() || null); // Reformat number when decimalPoints changes
    }
  }

  private format(input: string | null) {
    if (!input) return '';

    //make sure input is string
    input = input.toString();

    // Remove all non-digit and non-decimal point characters
    input = this.convertFaDigits(input);

    input = this.scientificToDecimal(input);

    let numberString = input.replace(/[^\d.]/g, '');

    // Check if the resulting string is a valid number
    if (!/^(?:\d+\.?|\d*\.\d*)$/.test(numberString)) {
      return ''; // Invalid number, return null or throw an error
    }

    if (this._max && Number(numberString) >= this._max) {
      numberString = this._max.toString();
    }

    // Split the string into integer and decimal parts
    const [integerPart = '', decimalPart = ''] = numberString.split('.');

    // Format the integer part by adding a thousands separator
    const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Limit the decimal part to a maximum of 8 decimal places
    const truncatedDecimalPart = decimalPart.slice(0, this._precision);

    // Combine the integer and decimal parts with the decimal separator
    let formattedNumber = formattedIntegerPart;
    if (truncatedDecimalPart.length > 0 || (numberString.includes('.') && this._precision > 0)) {
      formattedNumber += `.${truncatedDecimalPart}`;
    }

    return formattedNumber;
  }

  private unformat(value: string): number | null {
    // Unformat the value for the form control
    let unformattedValue = parseFloat(this.convertFaDigits(value).replace(/[^\d.]/g, ''));

    if (this._max && Number(unformattedValue) >= this._max) {
      unformattedValue = this._max;
    }

    return isNaN(unformattedValue) ? null : unformattedValue;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.el.nativeElement.setAttribute('disabled', isDisabled);
    }
  }
}
